/**
 * <ta-bulk-import> — drop a CSV of athletes, preview, and bulk-insert.
 *
 * CSV format (header row required):
 *   first_name, last_name, dob, throws_hand, joined_date, nickname
 *
 * Required:  first_name, last_name
 * Optional:  dob (YYYY-MM-DD), throws_hand (R|L|S, default R),
 *            joined_date (YYYY-MM-DD, default today), nickname
 *
 * Rows missing required columns are flagged in the preview and skipped
 * on import. Each athlete is inserted individually, so a UNIQUE
 * collision on (first_name, last_name) only fails THAT row — not the
 * whole batch.
 *
 * State machine:
 *   idle      → click to expand → picking
 *   picking   → drop / pick CSV → previewing
 *   previewing → click Import → importing
 *   importing → done → summary
 *   summary   → click Reset → idle
 *
 * Emits 'athletes-imported' (bubbling) when import finishes — parent
 * re-fetches the athletes list.
 */

import { bulkInsertAthletes } from '../../db/queries.js';
import { parseAthletesCsv } from '../../features/athletes/parse-csv.js';

class TaBulkImport extends HTMLElement {
  constructor() {
    super();
    this.state = 'idle';
    this._rows = [];          // parsed { row: {...}, ok: boolean, warnings: [] }
    this._unknownColumns = [];
    this._result = null;      // { added, duplicates, errors }
  }

  connectedCallback() {
    this.render();
    this._onClick = (e) => this.handleClick(e);
    this._onChange = (e) => this.handleChange(e);
    this._onDragOver = (e) => { e.preventDefault(); };
    this._onDrop = (e) => this.handleDrop(e);
    this.addEventListener('click', this._onClick);
    this.addEventListener('change', this._onChange);
    this.addEventListener('dragover', this._onDragOver);
    this.addEventListener('drop', this._onDrop);
  }

  handleClick(e) {
    if (e.target.closest('.ta-bulk__expand')) {
      e.preventDefault();
      this.state = 'picking';
      this.render();
    } else if (e.target.closest('.ta-bulk__cancel')) {
      e.preventDefault();
      this.reset();
    } else if (e.target.closest('.ta-bulk__import')) {
      e.preventDefault();
      this.runImport();
    } else if (e.target.closest('.ta-bulk__reset')) {
      e.preventDefault();
      this.reset();
    }
  }

  handleChange(e) {
    if (e.target.matches('input[type=file]')) {
      const file = e.target.files?.[0];
      if (file) this.processFile(file);
    }
  }

  handleDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && this.state === 'picking') this.processFile(file);
  }

  reset() {
    this.state = 'idle';
    this._rows = [];
    this._unknownColumns = [];
    this._result = null;
    this.render();
  }

  async processFile(file) {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please drop a .csv file.');
      return;
    }
    try {
      const text = await file.text();
      const parsed = parseAthletesCsv(text);
      this._rows = parsed.rows;
      this._unknownColumns = parsed.unknownColumns;
      this.state = 'previewing';
      this.render();
    } catch (err) {
      alert(`Couldn't parse CSV: ${err.message ?? err}`);
    }
  }

  async runImport() {
    const validRows = this._rows.filter((r) => r.ok).map((r) => r.row);
    if (validRows.length === 0) {
      alert('No valid rows to import.');
      return;
    }
    this.state = 'importing';
    this.render();
    try {
      this._result = await bulkInsertAthletes(validRows);
      this.state = 'summary';
      this.render();
      this.dispatchEvent(new CustomEvent('athletes-imported', { bubbles: true, detail: this._result }));
    } catch (err) {
      this.state = 'previewing';
      this.render();
      alert(`Import failed: ${err.message ?? err}`);
    }
  }

  render() {
    if (this.state === 'idle')        return this.renderIdle();
    if (this.state === 'picking')     return this.renderPicking();
    if (this.state === 'previewing')  return this.renderPreview();
    if (this.state === 'importing')   return this.renderImporting();
    if (this.state === 'summary')     return this.renderSummary();
  }

  renderIdle() {
    this.innerHTML = `
      <button type="button" class="ta-bulk__expand">+ Bulk import from CSV</button>
    `;
  }

  renderPicking() {
    this.innerHTML = `
      <div class="ta-bulk">
        <header class="ta-bulk__header">
          <h3 class="ta-bulk__title">Bulk import athletes</h3>
          <button type="button" class="ta-bulk__cancel">Cancel</button>
        </header>
        <p class="ta-bulk__sub">
          CSV with header row. Columns:
          <code>first_name, last_name, athlete_age, playing_age, throws_hand, nickname</code>
          (<code>dob</code> and <code>joined_date</code> also accepted as optional legacy).
          Only <code>first_name</code> and <code>last_name</code> are required.
        </p>
        <label class="ta-bulk__zone">
          <span>Drop CSV here, or click to browse</span>
          <input type="file" accept=".csv,text/csv" hidden>
        </label>
      </div>
    `;
  }

  renderPreview() {
    const valid = this._rows.filter((r) => r.ok).length;
    const invalid = this._rows.length - valid;

    this.innerHTML = `
      <div class="ta-bulk">
        <header class="ta-bulk__header">
          <h3 class="ta-bulk__title">Preview — ${this._rows.length} row${this._rows.length === 1 ? '' : 's'}</h3>
          <button type="button" class="ta-bulk__cancel">Cancel</button>
        </header>

        ${this._unknownColumns.length > 0 ? `
          <div class="ta-bulk__warn">
            Unknown columns ignored: ${this._unknownColumns.map((c) => `<code>${escapeHtml(c)}</code>`).join(', ')}
          </div>
        ` : ''}

        <p class="ta-bulk__stats">
          <strong>${valid}</strong> ready to import
          ${invalid > 0 ? ` · <strong>${invalid}</strong> invalid (will be skipped)` : ''}
        </p>

        <div class="ta-bulk__table-wrap">
          <table class="ta-bulk__table">
            <thead>
              <tr>
                <th></th>
                <th>First</th>
                <th>Last</th>
                <th>Age</th>
                <th>Playing</th>
                <th>Throws</th>
                <th>Nickname</th>
              </tr>
            </thead>
            <tbody>
              ${this._rows.map((r) => `
                <tr class="${r.ok ? 'is-ok' : 'is-bad'}">
                  <td class="ta-bulk__row-state">${r.ok ? '✓' : '✗'}</td>
                  <td>${escapeHtml(r.row.first_name ?? '')}</td>
                  <td>${escapeHtml(r.row.last_name ?? '')}</td>
                  <td>${r.row.athlete_age ?? ''}</td>
                  <td>${escapeHtml(r.row.playing_age ?? '')}</td>
                  <td>${escapeHtml(r.row.throws_hand ?? '')}</td>
                  <td>${escapeHtml(r.row.nickname ?? '')}</td>
                </tr>
                ${r.warnings.length > 0 ? `
                  <tr class="ta-bulk__warn-row">
                    <td colspan="7">${r.warnings.map(escapeHtml).join(' · ')}</td>
                  </tr>
                ` : ''}
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="form-actions">
          <button type="button" class="ta-bulk__cancel">Cancel</button>
          <button type="button" class="ta-bulk__import" ${valid === 0 ? 'disabled' : ''}>
            Import ${valid} athlete${valid === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    `;
  }

  renderImporting() {
    this.innerHTML = `
      <div class="ta-bulk">
        <p class="ta-bulk__importing">Importing… please wait.</p>
      </div>
    `;
  }

  renderSummary() {
    const r = this._result ?? { added: [], duplicates: [], errors: [] };
    this.innerHTML = `
      <div class="ta-bulk">
        <header class="ta-bulk__header">
          <h3 class="ta-bulk__title">Import complete</h3>
        </header>
        <p class="ta-bulk__stats">
          <strong>${r.added.length}</strong> added
          ${r.duplicates.length > 0 ? ` · <strong>${r.duplicates.length}</strong> duplicate${r.duplicates.length === 1 ? '' : 's'} skipped` : ''}
          ${r.errors.length > 0 ? ` · <strong>${r.errors.length}</strong> error${r.errors.length === 1 ? '' : 's'}` : ''}
        </p>

        ${r.duplicates.length > 0 ? `
          <details class="ta-bulk__details">
            <summary>Duplicates (${r.duplicates.length})</summary>
            <ul class="ta-bulk__list">
              ${r.duplicates.map((d) => `
                <li>${escapeHtml(d.row.first_name)} ${escapeHtml(d.row.last_name)} — already in roster</li>
              `).join('')}
            </ul>
          </details>
        ` : ''}

        ${r.errors.length > 0 ? `
          <details class="ta-bulk__details">
            <summary>Errors (${r.errors.length})</summary>
            <ul class="ta-bulk__list">
              ${r.errors.map((e) => `
                <li>
                  ${escapeHtml(e.row.first_name ?? '')} ${escapeHtml(e.row.last_name ?? '')} —
                  <span class="ta-bulk__error">${escapeHtml(e.error)}</span>
                </li>
              `).join('')}
            </ul>
          </details>
        ` : ''}

        <div class="form-actions">
          <button type="button" class="ta-bulk__reset">Done</button>
        </div>
      </div>
    `;
  }
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

customElements.define('ta-bulk-import', TaBulkImport);
